import { client } from '@/services/v2/client'
import { useQuery } from '@tanstack/react-query'
import { ProfileQueryKeys } from 'containers/profile/types/query-keys'
import {
  ProfileViewsRequestParams,
  ProfileViewsResponse,
} from '../types/profileViews'
import { useMemo } from 'react'
import { linkWithParams } from 'utilities/links'
import {
  useIsMod,
  useIsOwnProfile,
  useProfile,
  useSubscription,
} from 'containers/profile/hooks/useProfile'

const API_URL = '/api/v1/users'

export const useProfileViews = (params?: ProfileViewsRequestParams) => {
  const { profile } = useProfile()
  const { isSubscribed } = useSubscription()
  const isMod = useIsMod()
  const isOwnProfile = useIsOwnProfile()

  const showChart = useMemo(() => {
    let createdOn = profile?.timestamp
    let showByDate = false

    if (typeof createdOn === 'number') {
      // validate if date needs to be multiplied by 1000
      if (createdOn.toString().length === 10) {
        createdOn *= 1000
      }

      // Calculate 7 days after profile creation
      const date = new Date(createdOn)
      date.setDate(date.getDate() + 7)

      showByDate = Date.now() >= date.getTime()
    }

    return showByDate
  }, [profile])

  const url = useMemo(() => {
    return linkWithParams(`${API_URL}/profile-views/retrieve-profile-views`, {
      startDate: params?.startDate,
      endDate: params?.endDate,
    })
  }, [params])

  const { data, ...rest } = useQuery({
    queryKey: [
      ProfileQueryKeys.PROFILE,
      ProfileQueryKeys.VIEWS,
      url,
      isSubscribed,
    ],
    queryFn: () => client<ProfileViewsResponse>(url),
    enabled: isOwnProfile,
  })

  const chartData = useMemo(() => {
    if (!showChart) {
      return {
        ...data?.chartData,
        data: [],
        profileIds: [],
      }
    }

    return data?.chartData
  }, [data?.chartData, showChart])

  return {
    ...rest,
    data: {
      ...data,
      chartData,
    },
    isSubscribed: isSubscribed || isMod,
  }
}

interface ProfileViewers {
  public: string[]
  hidden: string[]
}

interface ProfileViewersData {
  chart: ProfileViewers
  totals: ProfileViewers
}

export const useProfilesViewers = (params?: ProfileViewsRequestParams) => {
  const { data, isSubscribed } = useProfileViews(params)

  return useMemo(() => {
    const viewers: ProfileViewersData = {
      chart: {
        public: data?.chartData?.profileIds || [],
        hidden: [],
      },
      totals: {
        public: data?.profileIds || [],
        hidden: [],
      },
    }

    if (!isSubscribed) {
      viewers.chart.public = data?.chartData?.profileIds?.slice(0, 3) || []
      viewers.chart.hidden = data?.profileIds?.slice(3, 10) || []

      viewers.totals.public = data?.profileIds?.slice(0, 3) || []
      viewers.totals.hidden = data?.profileIds?.slice(3, 6) || []
    }

    return viewers
  }, [data, isSubscribed])
}
